import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  getDriverById,
  verifyDriver,
  getDriverVehicleDetails,
  updateDriverVehicleDetails,
  getDriverDocuments,
  uploadDriverDocuments,
  getVehicleTypes,
  getDriverTasks,
  getDriverReviews,
  clearError,
  toggleDriverAvailabilityOptimistic,
} from "../features/driver/driverSlice";
import { Alert } from "react-bootstrap";
import {
  emailIcon,
  contactNumberIcon,
  BaseLocation,
  AddressBlackPin,
} from "../assets";
import CustomAlert from "../components/CustomAlert";
import { ShimmerBox } from "../components/Shimmer";
import "../css/Shimmer.css";
import addressIcon from "../assets/images/Formicons/address_icon.svg";
import scheduleIcon from "../assets/images/Formicons/shedule.svg";
import editPencilIcon from "../assets/images/Formicons/edit-pencil.svg";
import disabledIcon from "../assets/images/Formicons/disabled_icon.svg";
import taskCompletedIcon from "../assets/images/Driverimages/total-driver.svg";
import netEarningsIcon from "../assets/images/Driverimages/active-driver.svg";
import avgRatingIcon from "../assets/images/Driverimages/completed-jobs.svg";
import payoutIcon from "../assets/images/Driverimages/av-driver-rating.svg";
import xMarkIcon from "../assets/x-mark.svg";
import stopSignIcon from "../assets/images/Driverimages/stop-sign-driver.svg";
import notFoundEmptyIcon from "../assets/images/EmptyIcons/not-found-empty.svg";
import ratingsEmptyIcon from "../assets/images/EmptyIcons/Ratings-eampty.svg";
import successTickIcon from "../assets/success_tick.svg";
import "../css/DriverDetails.css";

const DriverDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const [activeTab, setActiveTab] = useState("tasks");
  const [showDisableModal, setShowDisableModal] = useState(false);
  const [showVehicleModal, setShowVehicleModal] = useState(false);
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [showVehicleSuccessModal, setShowVehicleSuccessModal] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [availability, setAvailability] = useState("AVAILABLE");

  // CustomAlert state
  const [customAlert, setCustomAlert] = useState({
    show: false,
    type: "success",
    title: "",
    message: "",
  });

  const showCustomAlert = (type, title, message) => {
    setCustomAlert({ show: true, type, title, message });
  };

  const closeCustomAlert = () => {
    setCustomAlert((prev) => ({ ...prev, show: false }));
  };

  // Auto-close Vehicle Success Modal after 10 seconds
  useEffect(() => {
    if (showVehicleSuccessModal) {
      const timer = setTimeout(() => {
        setShowVehicleSuccessModal(false);
      }, 10000);
      return () => clearTimeout(timer);
    }
  }, [showVehicleSuccessModal]);

  // Get driver data from Redux or location state
  const {
    selectedDriver,
    vehicleDetails,
    driverDocuments,
    driverTasks,
    driverReviews,
    vehicleTypes,
    isLoading,
    tasksLoading,
    reviewsLoading,
    error,
  } = useSelector((state) => state.driver);

  // Get driver ID from location state
  const driverId = location.state?.driver?.id;

  // Task filter state
  const [taskFilter] = useState("All");

  // Vehicle form state
  const [vehicleForm, setVehicleForm] = useState({
    vehicleTypeId: "",
    vehicleName: "",
    licenseNumber: "",
    modelYear: "",
  });

  // Vehicle form errors
  const [vehicleFormErrors, setVehicleFormErrors] = useState({
    vehicleTypeId: "",
    vehicleName: "",
    licenseNumber: "",
    modelYear: "",
  });

  // Document upload state
  const [documentFile, setDocumentFile] = useState(null);

  // Fetch driver details on mount
  useEffect(() => {
    if (driverId) {
      dispatch(getDriverById(driverId));
      dispatch(getDriverVehicleDetails(driverId));
      dispatch(getDriverDocuments(driverId));
      dispatch(getDriverTasks({ driverId }));
      dispatch(getDriverReviews(driverId));
    }
    // Fetch vehicle types for dropdown
    dispatch(getVehicleTypes());
  }, [dispatch, driverId]);

  // Fetch tasks when filter changes
  useEffect(() => {
    if (driverId && taskFilter) {
      const status =
        taskFilter === "All" ? undefined : taskFilter.toLowerCase();
      dispatch(getDriverTasks({ driverId, status }));
    }
  }, [dispatch, driverId, taskFilter]);

  // Set availability from driver data
  useEffect(() => {
    if (selectedDriver?.AvailabilityStatus) {
      setAvailability(selectedDriver.AvailabilityStatus);
    }
  }, [selectedDriver]);

  // Pre-fill vehicle form when data loads
  useEffect(() => {
    if (vehicleDetails) {
      // Find vehicleTypeId from vehicleTypes based on VehicleType name
      const matchingType = vehicleTypes?.find(
        (t) => t.Name === vehicleDetails.VehicleType,
      );

      // Helper to filter out "unknown" values
      const cleanValue = (val) => {
        if (!val || val === "unknown" || val === "Unknown") return "";
        return val;
      };

      setVehicleForm({
        vehicleTypeId: vehicleDetails.VehicleTypeId || matchingType?.Id || "",
        vehicleName: cleanValue(vehicleDetails.Brand),
        licenseNumber: cleanValue(selectedDriver?.LicenseNumber),
        modelYear: cleanValue(vehicleDetails.ModelYear),
      });
    }
  }, [vehicleDetails, vehicleTypes, selectedDriver]);

  // Clear error after 5 seconds
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        dispatch(clearError());
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, dispatch]);

  // Driver info from Redux or fallback
  const driverInfo = selectedDriver || location.state?.driver || {};

  // Get driver stats from tasks API response
  const driverStats = driverTasks?.Driver || {};

  // Stats data from API
  const stats = [
    {
      id: 1,
      label: "Task Completed",
      value: driverStats.TasksCompleted || driverInfo.TasksCompleted || 0,
      icon: taskCompletedIcon,
      color: "#8362E5",
    },
    {
      id: 2,
      label: "Net Earnings",
      value: `$${driverStats.TotalEarnings || driverInfo.TotalEarnings || 0}`,
      icon: netEarningsIcon,
      color: "#FF49CD",
    },
    {
      id: 3,
      label: "Average Rating",
      value: `${parseFloat(driverStats.AverageRating || driverInfo.AverageRating || 0).toFixed(1)} ⭐`,
      icon: avgRatingIcon,
      color: "#FDAF22",
    },
    {
      id: 4,
      label: "Total Reviews",
      value:
        driverStats.TotalReviews || driverReviews?.Driver?.TotalReviews || 0,
      icon: payoutIcon,
      color: "#32D484",
    },
  ];

  // Get tasks from API
  const tasks = driverTasks?.Tasks || [];
  // const taskStatusCounts = driverTasks?.StatusCounts || {};

  // Format vehicle details from API
  const getVehicleDetails = () => {
    if (!vehicleDetails) return null;

    // Helper to check if value is valid (not empty, null, or "unknown")
    const getValue = (val) => {
      if (!val || val === "" || val === "unknown" || val === "Unknown")
        return null;
      return val;
    };

    return {
      type: getValue(vehicleDetails.VehicleType) || "N/A",
      brand: getValue(vehicleDetails.Brand) || "N/A",
      licenseNumber: getValue(selectedDriver?.LicenseNumber) || "N/A",
      modelYear: getValue(vehicleDetails.ModelYear) || "N/A",
      number: getValue(vehicleDetails.NumberPlate) || "N/A",
      model: getValue(vehicleDetails.Model) || "N/A",
      capacity: vehicleDetails.Capacity || "N/A",
    };
  };

  const formattedVehicle = getVehicleDetails();

  // Format documents from API
  const getDocuments = () => {
    if (!driverDocuments) return [];

    const docMap = {
      DrivingLicense: "Driver's License",
      PCOLicense: "PCO License",
      Insurance: "Vehicle Insurance",
      VehicleRegistration: "Registration Copy",
      Signature: "Driver Signature",
    };

    return Object.entries(driverDocuments.Documents || {})
      .filter(([key, doc]) => key !== "Signature" && doc.Uploaded && doc.Url) // Hide Signature, only show uploaded documents
      .map(([key, doc]) => ({
        id: key,
        name: docMap[key] || key,
        hasFile: doc.Uploaded,
        url: doc.Url,
        verified: doc.Verified,
      }));
  };

  const documents = getDocuments();

  // Get ratings from API
  const ratings = driverReviews?.Reviews || [];

  // Handle edit driver (personal info)
  const handleEdit = () => {
    navigate("/edit-driver", {
      state: {
        driver: {
          driverId: driverInfo.ID,
          firstName: driverInfo.FirstName,
          lastName: driverInfo.LastName,
          mobileNo: driverInfo.Phone,
          email: driverInfo.Email,
          assignedBases: driverInfo.AssignedBases || [],
        },
      },
    });
  };

  // Handle edit vehicle - open modal
  const handleEditVehicle = () => {
    setShowVehicleModal(true);
  };

  // Handle vehicle form change
  const handleVehicleFormChange = (e) => {
    const { name, value } = e.target;
    setVehicleForm((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error when user starts typing
    if (vehicleFormErrors[name]) {
      setVehicleFormErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  // Validate vehicle form
  const validateVehicleForm = () => {
    const errors = {};

    if (!vehicleForm.vehicleTypeId) {
      errors.vehicleTypeId = "Please select a vehicle type";
    }

    if (!vehicleForm.vehicleName.trim()) {
      errors.vehicleName = "Vehicle name is required";
    }

    if (!vehicleForm.licenseNumber.trim()) {
      errors.licenseNumber = "License number is required";
    }

    if (!vehicleForm.modelYear) {
      errors.modelYear = "Model year is required";
    } else {
      const year = parseInt(vehicleForm.modelYear);
      const currentYear = new Date().getFullYear();
      if (year < 1900 || year > currentYear) {
        errors.modelYear = `Year must be between 1900 and ${currentYear}`;
      }
    }

    setVehicleFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle vehicle form submit
  const handleVehicleSubmit = async (e) => {
    e.preventDefault();

    if (!validateVehicleForm()) {
      return;
    }

    try {
      const updateData = {
        DriverId: parseInt(driverId),
        VehicleName: vehicleForm.vehicleName,
        LicenseNumber: vehicleForm.licenseNumber,
        VehicleTypeId: parseInt(vehicleForm.vehicleTypeId),
        ModelYear: parseInt(vehicleForm.modelYear),
      };

      await dispatch(updateDriverVehicleDetails(updateData)).unwrap();

      setShowVehicleModal(false);
      setShowVehicleSuccessModal(true);

      // Refresh vehicle details and driver details
      dispatch(getDriverVehicleDetails(driverId));
      dispatch(getDriverById(driverId));
    } catch (err) {
      showCustomAlert(
        "error",
        "Update Failed",
        "Failed to update vehicle details. Please try again.",
      );
    }
  };

  // Handle change document - open modal
  const handleChangeDocument = (doc) => {
    setSelectedDocument(doc);
    setShowDocumentModal(true);
    setDocumentFile(null);
  };

  // Handle document file change
  const handleDocumentFileChange = (e) => {
    setDocumentFile(e.target.files[0]);
  };

  // Handle document upload
  const handleDocumentUpload = async () => {
    if (!documentFile || !selectedDocument) {
      showCustomAlert("error", "No File Selected", "Please select a file");
      return;
    }

    try {
      // Map document ID to API field name (add "Doc" suffix)
      const docFieldMap = {
        DrivingLicense: "DrivingLicenseDoc",
        PCOLicense: "PCOLicenseDoc",
        Insurance: "InsuranceDoc",
        VehicleRegistration: "VehicleRegistrationDoc",
        Signature: "SignatureDoc",
      };

      const fieldName =
        docFieldMap[selectedDocument.id] || selectedDocument.id + "Doc";

      const formData = new FormData();
      formData.append("DriverId", String(driverId));
      formData.append(fieldName, documentFile);

      await dispatch(uploadDriverDocuments(formData)).unwrap();

      setShowDocumentModal(false);
      setSelectedDocument(null);
      setDocumentFile(null);
      showCustomAlert("success", "Success", "Document uploaded successfully!");

      // Refresh documents
      dispatch(getDriverDocuments(driverId));
    } catch (err) {
      showCustomAlert(
        "error",
        "Upload Failed",
        "Failed to upload document. Please try again.",
      );
    }
  };

  // Handle disable driver
  const handleDisableClick = () => {
    setShowDisableModal(true);
  };

  const handleDisableConfirm = async () => {
    try {
      await dispatch(
        verifyDriver({
          driverId: driverInfo.ID,
          accountStatus: "SUSPENDED",
        }),
      ).unwrap();

      setShowDisableModal(false);
      navigate("/driver", { replace: true });
    } catch (err) {
      // Failed to disable driver
    }
  };

  const handleDisableCancel = () => {
    setShowDisableModal(false);
  };

  // Handle availability change - Optimistic Update
  const handleAvailabilityChange = async (status) => {
    const previousAvailability = availability;

    // Step 1: Optimistic update - UI instantly changes
    setAvailability(status);
    dispatch(
      toggleDriverAvailabilityOptimistic({
        driverId: driverInfo.ID,
        newAvailability: status,
      }),
    );

    try {
      // Step 2: API call in background
      await dispatch(
        verifyDriver({
          driverId: driverInfo.ID,
          availabilityStatus: status,
        }),
      ).unwrap();
      // Success - no alert needed, UI already updated
    } catch (err) {
      // Step 3: Revert on error
      setAvailability(previousAvailability);
      dispatch(
        toggleDriverAvailabilityOptimistic({
          driverId: driverInfo.ID,
          newAvailability: previousAvailability,
        }),
      );
      showCustomAlert(
        "error",
        "Update Failed",
        "Failed to update availability. Please try again.",
      );
    }
  };

  // Handle save availability - No longer needed as click directly updates
  const handleSave = async () => {
    // Now availability updates happen on button click itself
    showCustomAlert("success", "Success", "Availability saved!");
  };

  // Handle view document
  const handleViewDocument = (url) => {
    if (url) {
      window.open(url, "_blank");
    } else {
      showCustomAlert("error", "Not Available", "Document not available");
    }
  };

  // Render stars
  const renderStars = (rating) => {
    const stars = [];
    for (let i = 0; i < 5; i++) {
      if (i < rating) {
        stars.push(
          <span key={i} className="star filled">
            ⭐
          </span>,
        );
      } else {
        stars.push(
          <span key={i} className="star empty">
            ☆
          </span>,
        );
      }
    }
    return stars;
  };

  // Loading state - Shimmer
  if (isLoading && !driverInfo.ID) {
    return (
      <div className="driver-details-page">
        <div className="details-container">
          {/* Driver Header Shimmer */}
          <div className="driver-header-card">
            <div className="driver-profile">
              <div className="profile-left">
                <ShimmerBox width="80px" height="80px" borderRadius="50%" />
                <div className="profile-info">
                  <ShimmerBox width="180px" height="24px" />
                  <div className="contact-info" style={{ marginTop: "12px" }}>
                    <ShimmerBox width="120px" height="14px" />
                    <ShimmerBox width="160px" height="14px" />
                  </div>
                </div>
              </div>
              <div className="profile-right">
                <ShimmerBox width="100px" height="32px" borderRadius="6px" />
              </div>
            </div>
          </div>

          <div className="details-content">
            {/* Left Content Shimmer */}
            <div className="main-content">
              <div className="tabs-container">
                {[1, 2, 3, 4].map((item) => (
                  <ShimmerBox
                    key={item}
                    width="80px"
                    height="36px"
                    borderRadius="6px"
                  />
                ))}
              </div>
              <div className="tab-content">
                <div className="tasks-section">
                  <div className="tasks-header">
                    {[1, 2, 3, 4, 5].map((item) => (
                      <ShimmerBox key={item} width="80px" height="14px" />
                    ))}
                  </div>
                  {[1, 2, 3].map((item) => (
                    <div
                      key={item}
                      className="booking-card"
                      style={{ padding: "16px", marginBottom: "12px" }}
                    >
                      <div
                        className="booking-main"
                        style={{
                          display: "flex",
                          gap: "20px",
                          alignItems: "center",
                        }}
                      >
                        <ShimmerBox width="70px" height="14px" />
                        <ShimmerBox width="120px" height="14px" />
                        <ShimmerBox width="60px" height="14px" />
                        <ShimmerBox
                          width="70px"
                          height="24px"
                          borderRadius="4px"
                        />
                        <ShimmerBox
                          width="90px"
                          height="32px"
                          borderRadius="6px"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Right Sidebar Shimmer */}
            <div className="stats-sidebar">
              {[1, 2, 3, 4].map((item) => (
                <div
                  key={item}
                  className="stat-card"
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    padding: "16px",
                  }}
                >
                  <div>
                    <ShimmerBox width="80px" height="14px" />
                    <ShimmerBox
                      width="60px"
                      height="24px"
                      style={{ marginTop: "8px" }}
                    />
                  </div>
                  <ShimmerBox width="32px" height="32px" borderRadius="6px" />
                </div>
              ))}
              <div className="availability-card" style={{ padding: "16px" }}>
                <ShimmerBox width="100px" height="18px" />
                <div
                  style={{ display: "flex", gap: "10px", marginTop: "12px" }}
                >
                  <ShimmerBox width="80px" height="36px" borderRadius="6px" />
                  <ShimmerBox width="80px" height="36px" borderRadius="6px" />
                </div>
                <div
                  style={{ display: "flex", gap: "10px", marginTop: "10px" }}
                >
                  <ShimmerBox width="80px" height="36px" borderRadius="6px" />
                  <ShimmerBox width="80px" height="36px" borderRadius="6px" />
                </div>
                <ShimmerBox
                  width="100%"
                  height="40px"
                  borderRadius="6px"
                  style={{ marginTop: "16px" }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="driver-details-page">
      <div className="details-container">
        {/* Error Alert */}
        {error && (
          <Alert
            variant="danger"
            dismissible
            onClose={() => dispatch(clearError())}
          >
            {error}
          </Alert>
        )}

        {/* Driver Header Card */}
        <div className="driver-header-card">
          <div className="driver-profile">
            <div className="profile-left">
              <div className="profile-image">
                {driverInfo.ProfileImage ? (
                  <img
                    src={driverInfo.ProfileImage}
                    alt={driverInfo.FullName}
                  />
                ) : (
                  "👤"
                )}
              </div>
              <div className="profile-info">
                <h2 className="driver-name">
                  {driverInfo.FullName || driverInfo.name}
                </h2>
                <div className="contact-info">
                  <div className="contact-item">
                    <img src={contactNumberIcon} alt="" className="icon" />
                    <span>{driverInfo.Phone || driverInfo.phone}</span>
                  </div>
                 
                  <div className="contact-item">
                    <img src={AddressBlackPin} alt="" className="icon" />
                    <span>
                      {(driverInfo.AssignedBases || []).length > 0
                        ? driverInfo.AssignedBases.map(
                            (b) => `${b.BaseName}, ${b.City}`,
                          ).join(" | ")
                        : driverInfo.BaseLocation || "—"}
                    </span>
                  </div>
                </div>
              </div>
              {/* Mobile: Edit/Disable/Rating next to image */}
              <div className="profile-right-mobile">
                <div className="action-icons">
                  <button
                    className="icon-btn edit-btn"
                    onClick={handleEdit}
                    title="Edit Driver"
                  >
                    <img src={editPencilIcon} alt="Edit" />
                  </button>
                  <span className="action-divider"></span>
                  <button
                    className="icon-btn disable-btn"
                    onClick={handleDisableClick}
                    title={
                      driverInfo.AccountStatus === "INVITED"
                        ? "Driver must accept invitation first"
                        : "Disable Driver"
                    }
                    disabled={driverInfo.AccountStatus === "INVITED"}
                    style={{
                      opacity: driverInfo.AccountStatus === "INVITED" ? 0.5 : 1,
                      cursor:
                        driverInfo.AccountStatus === "INVITED"
                          ? "not-allowed"
                          : "pointer",
                    }}
                  >
                    <img src={disabledIcon} alt="Disable" />
                  </button>
                </div>
                <span className="rating-badge">
                  Rating:{" "}
                  {parseFloat(
                    driverInfo.AverageRating || driverInfo.rating || 0,
                  ).toFixed(1)}{" "}
                  ⭐
                </span>
              </div>
            </div>
            {/* Mobile: Name and contact info below */}
            <div className="profile-info-mobile">
              <h2 className="driver-name">
                {driverInfo.FullName || driverInfo.name}
              </h2>
              <div className="contact-info">
                <div className="contact-item">
                  <img src={contactNumberIcon} alt="" className="icon" />
                  <span>{driverInfo.Phone || driverInfo.phone}</span>
                </div>
                <div className="contact-item">
                  <img src={emailIcon} alt="" className="icon" />
                  <span>{driverInfo.Email || driverInfo.email}</span>
                </div>
              </div>
            </div>
            <div className="profile-right">
              <div className="action-icons">
                <button
                  className="icon-btn edit-btn"
                  onClick={handleEdit}
                  title="Edit Driver"
                >
                  <img src={editPencilIcon} alt="Edit" />
                </button>
                <span className="action-divider"></span>
                <button
                  className="icon-btn disable-btn"
                  onClick={handleDisableClick}
                  title={
                    driverInfo.AccountStatus === "INVITED"
                      ? "Driver must accept invitation first"
                      : "Disable Driver"
                  }
                  disabled={driverInfo.AccountStatus === "INVITED"}
                  style={{
                    opacity: driverInfo.AccountStatus === "INVITED" ? 0.5 : 1,
                    cursor:
                      driverInfo.AccountStatus === "INVITED"
                        ? "not-allowed"
                        : "pointer",
                  }}
                >
                  <img src={disabledIcon} alt="Disable" />
                </button>
              </div>
              <span className="rating-badge">
                Rating:{" "}
                {parseFloat(
                  driverInfo.AverageRating || driverInfo.rating || 0,
                ).toFixed(1)}{" "}
                ⭐
              </span>
            </div>
          </div>
        </div>

        <div className="details-content">
          {/* Left Side - Tabs Content */}
          <div className="main-content">
            {/* Tabs */}
            <div className="tabs-container">
              <button
                className={`tab-btn ${activeTab === "tasks" ? "active" : ""}`}
                onClick={() => setActiveTab("tasks")}
              >
                Tasks
              </button>
              <button
                className={`tab-btn ${activeTab === "vehicle" ? "active" : ""}`}
                onClick={() => setActiveTab("vehicle")}
              >
                Vehicle Details
              </button>
              <button
                className={`tab-btn ${activeTab === "documents" ? "active" : ""}`}
                onClick={() => setActiveTab("documents")}
              >
                Documents
              </button>
              <button
                className={`tab-btn ${activeTab === "ratings" ? "active" : ""}`}
                onClick={() => setActiveTab("ratings")}
              >
                Ratings
              </button>
            </div>

            {/* Tab Content */}
            <div className="tab-content">
              {/* Tasks Tab */}
              {activeTab === "tasks" && (
                <div className="tasks-section">
                  {/* Filter Tabs - Hidden for now */}
                  {/* <div className="task-filters">
                    {['All', 'New', 'Ongoing', 'Completed', 'Cancelled'].map((filter) => (
                      <button
                        key={filter}
                        className={`filter-btn ${taskFilter === filter ? 'active' : ''}`}
                        onClick={() => setTaskFilter(filter)}
                      >
                        {filter} {taskStatusCounts[filter] !== undefined ? `(${taskStatusCounts[filter]})` : ''}
                      </button>
                    ))}
                  </div> */}

                  {/* Header Row */}
                  <div className="tasks-header">
                    <span className="header-col">Booking ID</span>
                    <span className="header-col">Service Request</span>
                    <span className="header-col">Amount</span>
                    <span className="header-col">Status</span>
                    <span className="header-col">Action</span>
                  </div>

                  {/* Booking Cards */}
                  <div className="booking-cards">
                    {tasksLoading ? (
                      <div className="shimmer-tasks-loading">
                        {[1, 2, 3].map((item) => (
                          <div
                            key={item}
                            className="booking-card"
                            style={{ padding: "16px", marginBottom: "12px" }}
                          >
                            <div
                              className="booking-main"
                              style={{
                                display: "flex",
                                gap: "20px",
                                alignItems: "center",
                              }}
                            >
                              <ShimmerBox width="70px" height="14px" />
                              <ShimmerBox width="120px" height="14px" />
                              <ShimmerBox width="60px" height="14px" />
                              <ShimmerBox
                                width="70px"
                                height="24px"
                                borderRadius="4px"
                              />
                              <ShimmerBox
                                width="90px"
                                height="32px"
                                borderRadius="6px"
                              />
                            </div>
                            <div
                              className="booking-details"
                              style={{
                                display: "flex",
                                gap: "20px",
                                marginTop: "12px",
                              }}
                            >
                              <ShimmerBox width="150px" height="14px" />
                              <ShimmerBox width="120px" height="14px" />
                              <ShimmerBox width="80px" height="14px" />
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : tasks.length > 0 ? (
                      tasks.map((task, index) => (
                        <div
                          key={task.TaskId || index}
                          className="booking-card"
                        >
                          <div className="booking-main">
                            <span className="booking-id">
                              {task.BookingIdDisplay || `#${task.BookingId}`}
                            </span>
                            <div className="service-info">
                              <span className="service-name">
                                {task.ServiceName || task.ServiceRequest}
                              </span>
                              <span className="service-date">
                                {task.ServiceDateTime}
                              </span>
                            </div>
                            <span className="amount">
                              {task.AmountDisplay || `$${task.Amount}`}
                            </span>
                            <span
                              className={`status-badge status-${(task.Status || "").toLowerCase()}`}
                            >
                              {task.Status}
                            </span>
                            <button
                              className="view-details-btn"
                              onClick={() =>
                                navigate(`/booking-details/${task.BookingId}`)
                              }
                            >
                              View Details
                            </button>
                          </div>
                          <div className="booking-details">
                            <div className="detail-item">
                              <img
                                src={addressIcon}
                                alt=""
                                className="detail-icon"
                              />
                              <span>
                                {task.DeliveryLocation ||
                                  `Delivery: ${task.DeliveryAddress}`}
                              </span>
                            </div>
                            <div className="detail-item">
                              <img
                                src={scheduleIcon}
                                alt=""
                                className="detail-icon"
                              />
                              <span>
                                {task.DateLabel}:{" "}
                                {task.DateValue || task.ServiceDateTime}
                              </span>
                            </div>
                            <div className="detail-item rating-item">
                              <span>Rating:</span>
                              <div className="stars">
                                {renderStars(task.Rating || 0)}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="no-tasks-state">
                        <img
                          src={notFoundEmptyIcon}
                          alt="No Tasks"
                          className="empty-state-icon"
                        />
                        <h3>No Tasks Found</h3>
                        <p>
                          There are no{" "}
                          {taskFilter !== "All" ? taskFilter.toLowerCase() : ""}{" "}
                          tasks for this driver.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Vehicle Details Tab */}
              {activeTab === "vehicle" && (
                <div className="vehicle-section">
                  <div className="vehicle-header">
                    <h3>Vehicle Details</h3>
                    <button
                      className="edit-btn-vehicle"
                      onClick={handleEditVehicle}
                    >
                      Edit <img src={editPencilIcon} alt="" />
                    </button>
                  </div>

                  {isLoading ? (
                    <div className="shimmer-vehicle-loading">
                      {[1, 2, 3, 4].map((item) => (
                        <div key={item} className="vehicle-row">
                          <ShimmerBox width="100px" height="14px" />
                          <ShimmerBox width="120px" height="14px" />
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="vehicle-details-list">
                      <div className="vehicle-row">
                        <span className="vehicle-label">Vehicle Type</span>
                        <span className="vehicle-value">
                          {formattedVehicle?.type || "N/A"}
                        </span>
                      </div>
                      <div className="vehicle-row">
                        <span className="vehicle-label">Vehicle Name</span>
                        <span className="vehicle-value">
                          {formattedVehicle?.brand || "N/A"}
                        </span>
                      </div>
                      <div className="vehicle-row">
                        <span className="vehicle-label">License Number</span>
                        <span className="vehicle-value">
                          {formattedVehicle?.licenseNumber || "N/A"}
                        </span>
                      </div>
                      <div className="vehicle-row">
                        <span className="vehicle-label">Model Year</span>
                        <span className="vehicle-value">
                          {formattedVehicle?.modelYear || "N/A"}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Documents Tab */}
              {activeTab === "documents" && (
                <div className="documents-section">
                  <h3>Documents</h3>

                  {isLoading ? (
                    <div className="documents-grid">
                      {[1, 2, 3, 4].map((item) => (
                        <div
                          key={item}
                          className="document-card"
                          style={{ padding: "16px" }}
                        >
                          <ShimmerBox width="120px" height="16px" />
                          <ShimmerBox
                            width="100%"
                            height="80px"
                            borderRadius="8px"
                            style={{ marginTop: "12px" }}
                          />
                          <div
                            style={{
                              display: "flex",
                              gap: "10px",
                              marginTop: "12px",
                            }}
                          >
                            <ShimmerBox
                              width="60px"
                              height="28px"
                              borderRadius="4px"
                            />
                            <ShimmerBox
                              width="60px"
                              height="28px"
                              borderRadius="4px"
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : documents.length > 0 ? (
                    <div className="documents-grid">
                      {documents.map((doc) => (
                        <div key={doc.id} className="document-card">
                          <h4>{doc.name}</h4>
                          <div className="document-preview">
                            {doc.hasFile ? (
                              <>
                                <span className="doc-icon">📄</span>
                                {doc.verified && (
                                  <span className="verified-badge">
                                    ✓ Verified
                                  </span>
                                )}
                              </>
                            ) : (
                              <span className="no-file">No file</span>
                            )}
                          </div>
                          <div className="document-actions">
                            <button
                              className="doc-btn change-btn"
                              onClick={() => handleChangeDocument(doc)}
                            >
                              Change
                            </button>
                            <button
                              className="doc-btn view-btn"
                              onClick={() => handleViewDocument(doc.url)}
                              disabled={!doc.hasFile}
                            >
                              View
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="no-documents-state">
                      <img
                        src={notFoundEmptyIcon}
                        alt="No Documents"
                        className="empty-state-icon"
                      />
                      <h3>No Documents Found</h3>
                      <p>This driver hasn't uploaded any documents yet.</p>
                    </div>
                  )}
                </div>
              )}

              {/* Ratings Tab */}
              {activeTab === "ratings" && (
                <div className="ratings-section">
                  {reviewsLoading ? (
                    <table className="ratings-table">
                      <thead>
                        <tr>
                          <th>
                            <ShimmerBox width="70px" height="14px" />
                          </th>
                          <th>
                            <ShimmerBox width="70px" height="14px" />
                          </th>
                          <th>
                            <ShimmerBox width="60px" height="14px" />
                          </th>
                          <th>
                            <ShimmerBox width="60px" height="14px" />
                          </th>
                          <th>
                            <ShimmerBox width="80px" height="14px" />
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {[1, 2, 3, 4].map((item) => (
                          <tr key={item}>
                            <td>
                              <ShimmerBox width="60px" height="14px" />
                            </td>
                            <td>
                              <ShimmerBox width="80px" height="14px" />
                            </td>
                            <td>
                              <ShimmerBox width="80px" height="14px" />
                            </td>
                            <td>
                              <ShimmerBox width="100px" height="14px" />
                            </td>
                            <td>
                              <ShimmerBox width="150px" height="14px" />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : ratings.length > 0 ? (
                    <table className="ratings-table">
                      <thead>
                        <tr>
                          <th>Booking Id</th>
                          <th>Customer</th>
                          <th>Service</th>
                          <th>Ratings</th>
                          <th>Comments</th>
                        </tr>
                      </thead>
                      <tbody>
                        {ratings.map((rating) => (
                          <tr key={rating.ReviewId}>
                            <td>
                              {rating.BookingNumber || `#${rating.BookingId}`}
                            </td>
                            <td>{rating.CustomerName}</td>
                            <td>{rating.ServiceName}</td>
                            <td>
                              <div className="rating-cell">
                                <div className="stars">
                                  {renderStars(
                                    rating.DriverRating || rating.Rating,
                                  )}
                                </div>
                                {(rating.DriverRating || rating.Rating) > 0 && (
                                  <span className="rating-text">
                                    Rated {rating.DriverRating || rating.Rating}{" "}
                                    out of 5
                                  </span>
                                )}
                              </div>
                            </td>
                            <td>{rating.Review}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <div className="no-ratings-state">
                      <img
                        src={ratingsEmptyIcon}
                        alt="No Reviews"
                        className="empty-state-icon"
                      />
                      <h3>No Reviews Yet</h3>
                      <p>This driver hasn't received any reviews yet.</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Right Sidebar - Stats */}
          <div className="stats-sidebar">
            {stats.map((stat) => (
              <div key={stat.id} className="stat-card">
                <div className="stat-info-side">
                  <p className="stat-label">{stat.label}</p>
                  <h3 className="stat-value">{stat.value}</h3>
                </div>
                <div className="stat-icon">
                  <img src={stat.icon} alt="" />
                </div>
              </div>
            ))}

            {/* Availability Section */}
            <div className="availability-card">
              <h4>Availability</h4>
              <div className="availability-options">
                <button
                  className={`availability-btn ${availability === "AVAILABLE" ? "active" : ""}`}
                  onClick={() => handleAvailabilityChange("AVAILABLE")}
                >
                  Available
                </button>
                <button
                  className={`availability-btn ${availability === "ON_TRIP" ? "active" : ""}`}
                  onClick={() => handleAvailabilityChange("ON_TRIP")}
                >
                  On Trip
                </button>
                <button
                  className={`availability-btn ${availability === "PAUSE" ? "active" : ""}`}
                  onClick={() => handleAvailabilityChange("PAUSE")}
                >
                  Pause
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Vehicle Edit Modal */}
      {showVehicleModal && (
        <div className="modal-overlay-disable">
          <div className="edit-modal">
            <button
              className="close-modal-btn"
              onClick={() => setShowVehicleModal(false)}
            >
              <img src={xMarkIcon} alt="Close" />
            </button>

            <h2 className="modal-title">Edit Vehicle Details</h2>

            <form
              onSubmit={handleVehicleSubmit}
              className="modal-form"
              noValidate
            >
              <div className="form-row-modal">
                <div className="form-group-modal">
                  <label>Vehicle Type</label>
                  <select
                    name="vehicleTypeId"
                    value={vehicleForm.vehicleTypeId}
                    onChange={handleVehicleFormChange}
                    className={
                      vehicleFormErrors.vehicleTypeId ? "input-error" : ""
                    }
                  >
                    <option value="">Select Vehicle Type</option>
                    {vehicleTypes?.map((type) => (
                      <option key={type.Id} value={type.Id}>
                        {type.Name}
                      </option>
                    ))}
                  </select>
                  {vehicleFormErrors.vehicleTypeId && (
                    <span className="error-message">
                      {vehicleFormErrors.vehicleTypeId}
                    </span>
                  )}
                </div>

                <div className="form-group-modal">
                  <label>Vehicle Name</label>
                  <input
                    type="text"
                    name="vehicleName"
                    value={vehicleForm.vehicleName}
                    onChange={handleVehicleFormChange}
                    placeholder="e.g., Toyota"
                    className={
                      vehicleFormErrors.vehicleName ? "input-error" : ""
                    }
                  />
                  {vehicleFormErrors.vehicleName && (
                    <span className="error-message">
                      {vehicleFormErrors.vehicleName}
                    </span>
                  )}
                </div>
              </div>

              <div className="form-row-modal">
                <div className="form-group-modal">
                  <label>License Number</label>
                  <input
                    type="text"
                    name="licenseNumber"
                    value={vehicleForm.licenseNumber}
                    onChange={handleVehicleFormChange}
                    placeholder="e.g., DL999888"
                    className={
                      vehicleFormErrors.licenseNumber ? "input-error" : ""
                    }
                  />
                  {vehicleFormErrors.licenseNumber && (
                    <span className="error-message">
                      {vehicleFormErrors.licenseNumber}
                    </span>
                  )}
                </div>

                <div className="form-group-modal">
                  <label>Model Year</label>
                  <input
                    type="number"
                    name="modelYear"
                    value={vehicleForm.modelYear}
                    onChange={handleVehicleFormChange}
                    placeholder="e.g., 2024"
                    min="1900"
                    max={new Date().getFullYear()}
                    className={vehicleFormErrors.modelYear ? "input-error" : ""}
                  />
                  {vehicleFormErrors.modelYear && (
                    <span className="error-message">
                      {vehicleFormErrors.modelYear}
                    </span>
                  )}
                </div>
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  className="vehicle-modal-cancel-btn"
                  onClick={() => setShowVehicleModal(false)}
                >
                  CANCEL
                </button>
                <button
                  type="submit"
                  className="vehicle-modal-save-btn"
                  disabled={isLoading}
                >
                  {isLoading ? "SAVING..." : "SAVE CHANGES"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Vehicle Success Modal */}
      {showVehicleSuccessModal && (
        <div
          className="vehicle-success-overlay"
          onClick={() => setShowVehicleSuccessModal(false)}
        >
          <div
            className="vehicle-success-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="vehicle-success-close"
              onClick={() => setShowVehicleSuccessModal(false)}
            >
              <img src={xMarkIcon} alt="Close" width="24" height="24" />
            </button>
            <div className="vehicle-success-icon">
              <img src={successTickIcon} alt="Success" />
            </div>
            <h2 className="vehicle-success-title">Updated Successfully!</h2>
            <p className="vehicle-success-message">
              Vehicle details have been updated successfully.
            </p>
            <button
              className="vehicle-success-btn"
              onClick={() => setShowVehicleSuccessModal(false)}
            >
              DONE
            </button>
          </div>
        </div>
      )}

      {/* Document Upload Modal */}
      {showDocumentModal && selectedDocument && (
        <div
          className="modal-overlay-disable"
          onClick={() => setShowDocumentModal(false)}
        >
          <div
            className="change-doc-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="close-modal-btn"
              onClick={() => setShowDocumentModal(false)}
            >
              ✕
            </button>

            <div className="modal-section">
              <h3 className="modal-section-title">Change Document</h3>
              <p className="modal-subtitle">
                Update your <strong>{selectedDocument.name}</strong>
              </p>

              {/* Current Document Preview */}
              {selectedDocument.url && (
                <div className="current-doc-preview">
                  <p className="preview-label">Current Document:</p>
                  <div className="preview-card">
                    <img
                      src={selectedDocument.url}
                      alt={selectedDocument.name}
                      style={{
                        width: "100%",
                        height: "200px",
                        objectFit: "cover",
                        borderRadius: "8px",
                      }}
                      onError={(e) => {
                        e.target.style.display = "none";
                        e.target.parentElement.innerHTML =
                          '<div style="display:flex;align-items:center;justify-content:center;height:200px;color:#999;background:#F8FAFC;border-radius:8px;">📄</div>';
                      }}
                    />
                  </div>
                </div>
              )}

              {/* Upload New Document */}
              <div className="upload-new-doc">
                <p className="upload-label">Upload New Document:</p>
                <div className="file-upload-box">
                  <input
                    type="file"
                    id="documentFile"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={handleDocumentFileChange}
                    hidden
                  />
                  <label
                    htmlFor="documentFile"
                    className="file-upload-label-new"
                  >
                    <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
                      <path
                        d="M33.3333 25V31.6667C33.3333 32.5507 32.9821 33.3986 32.357 34.0237C31.7319 34.6488 30.8841 35 30 35H10C9.11594 35 8.2681 34.6488 7.64298 34.0237C7.01786 33.3986 6.66667 32.5507 6.66667 31.6667V25"
                        stroke="#6366F1"
                        strokeWidth="3"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M26.6667 13.3333L20 6.66666L13.3333 13.3333"
                        stroke="#6366F1"
                        strokeWidth="3"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M20 6.66666V25"
                        stroke="#6366F1"
                        strokeWidth="3"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    <span className="upload-text">
                      {documentFile
                        ? documentFile.name
                        : "Click to upload or drag and drop"}
                    </span>
                    <span className="upload-hint">
                      PDF, JPG, JPEG or PNG (Max 10MB)
                    </span>
                  </label>
                </div>
              </div>
            </div>

            <button
              className="modal-submit-btn"
              onClick={handleDocumentUpload}
              disabled={!documentFile || isLoading}
            >
              {isLoading ? "UPLOADING..." : "SAVE CHANGES"}
            </button>
          </div>
        </div>
      )}

      {/* Disable Driver Modal */}
      {showDisableModal && (
        <div className="modal-overlay-disable">
          <div className="disable-modal">
            <button className="close-modal-btn" onClick={handleDisableCancel}>
              <img src={xMarkIcon} alt="Close" />
            </button>

            <div className="disable-icon">
              <img src={stopSignIcon} alt="Disable" />
            </div>

            <h2 className="disable-title">
              Are you sure you want to disable driver?
            </h2>

            <div className="modal-actions">
              <button
                className="modal-btn cancel-btn"
                onClick={handleDisableCancel}
              >
                CANCEL
              </button>
              <button
                className="modal-btn disable-confirm-btn"
                onClick={handleDisableConfirm}
              >
                DISABLE
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Custom Alert */}
      <CustomAlert
        show={customAlert.show}
        onClose={closeCustomAlert}
        type={customAlert.type}
        title={customAlert.title}
        message={customAlert.message}
      />
    </div>
  );
};

export default DriverDetails;
